/**
 * Domains Tab Component
 * Displays and manages whitelisted domains
 */

import { useState, useEffect } from 'react';
import { Section } from '../../layout/Section';
import { AddDomainModal } from './AddDomainModal';
import {
  getWhitelistedDomains,
  addWhitelistedDomain,
  deleteWhitelistedDomain,
  updateWhitelistedDomain,
} from '../../../services/api/access-control';
import type { WhitelistedDomain, CreateDomainRequest } from '../../../types/access-control';

export function DomainsTab() {
  const [domains, setDomains] = useState<WhitelistedDomain[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);

  // Fetch domains on mount
  useEffect(() => {
    fetchDomains();
  }, []);

  const fetchDomains = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getWhitelistedDomains();
      setDomains(response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load domains');
    } finally {
      setLoading(false);
    }
  };

  const handleAddDomain = async (data: CreateDomainRequest) => {
    await addWhitelistedDomain(data);
    await fetchDomains();
  };

  const handleDeleteDomain = async (id: string, domain: string) => {
    if (!confirm(`Are you sure you want to remove the whitelisted domain "${domain}"?`)) {
      return;
    }

    try {
      await deleteWhitelistedDomain(id);
      await fetchDomains();
    } catch (err) {
      alert(`Failed to delete domain: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const handleToggleAutoApprove = async (domainItem: WhitelistedDomain) => {
    try {
      await updateWhitelistedDomain(domainItem.id, {
        autoApprove: !domainItem.autoApprove,
      });
      await fetchDomains();
    } catch (err) {
      alert(`Failed to update domain: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">Loading domains...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        <p className="font-semibold">Error loading domains</p>
        <p className="text-sm mt-1">{error}</p>
        <button
          onClick={fetchDomains}
          className="mt-2 text-sm text-red-800 underline hover:no-underline"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-gray-600">
          {domains.length} whitelisted {domains.length === 1 ? 'domain' : 'domains'}
        </p>
        <button
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2 text-sm font-semibold bg-indigo-600 text-white rounded hover:bg-indigo-700"
        >
          + Add Domain
        </button>
      </div>

      {domains.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded p-8 text-center">
          <p className="text-gray-600 mb-2">No whitelisted domains yet</p>
          <p className="text-sm text-gray-500">
            Add a domain to automatically grant or manage access for users from that organization
          </p>
        </div>
      ) : (
        <Section title="Whitelisted Domains">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr>
                  <th className="bg-indigo-950 text-indigo-100 px-3 py-2 font-semibold text-left">
                    Domain
                  </th>
                  <th className="bg-indigo-950 text-indigo-100 px-3 py-2 font-semibold text-center">
                    Auto-Approve
                  </th>
                  <th className="bg-indigo-950 text-indigo-100 px-3 py-2 font-semibold text-left">
                    Description
                  </th>
                  <th className="bg-indigo-950 text-indigo-100 px-3 py-2 font-semibold text-left">
                    Created By
                  </th>
                  <th className="bg-indigo-950 text-indigo-100 px-3 py-2 font-semibold text-left">
                    Created
                  </th>
                  <th className="bg-indigo-950 text-indigo-100 px-3 py-2 font-semibold text-center">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {domains.map((domain, index) => (
                  <tr
                    key={domain.id}
                    className={index % 2 === 0 ? 'bg-purple-50' : 'bg-white'}
                  >
                    <td className="px-3 py-2 border-b border-gray-200 text-left font-medium">
                      {domain.domain}
                    </td>
                    <td className="px-3 py-2 border-b border-gray-200 text-center">
                      <button
                        onClick={() => handleToggleAutoApprove(domain)}
                        className={`
                          relative inline-flex h-6 w-11 items-center rounded-full
                          transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2
                          ${domain.autoApprove ? 'bg-indigo-600' : 'bg-gray-200'}
                        `}
                        title={domain.autoApprove ? 'Auto-approve enabled' : 'Auto-approve disabled'}
                      >
                        <span
                          className={`
                            inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                            ${domain.autoApprove ? 'translate-x-6' : 'translate-x-1'}
                          `}
                        />
                      </button>
                    </td>
                    <td className="px-3 py-2 border-b border-gray-200 text-left">
                      <span className="text-gray-600">
                        {domain.description || (
                          <em className="text-gray-400">No description</em>
                        )}
                      </span>
                    </td>
                    <td className="px-3 py-2 border-b border-gray-200 text-left">
                      <span className="text-gray-600">
                        {domain.createdBy || (
                          <em className="text-gray-400">Unknown</em>
                        )}
                      </span>
                    </td>
                    <td className="px-3 py-2 border-b border-gray-200 text-left">
                      <span className="text-gray-600">{formatDate(domain.createdAt)}</span>
                    </td>
                    <td className="px-3 py-2 border-b border-gray-200 text-center">
                      <button
                        onClick={() => handleDeleteDomain(domain.id, domain.domain)}
                        className="text-red-600 hover:text-red-800 font-medium"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {showAddModal && (
        <AddDomainModal
          onClose={(saved) => {
            setShowAddModal(false);
            if (saved) fetchDomains();
          }}
          onSubmit={handleAddDomain}
        />
      )}
    </div>
  );
}
